import React from "react";
import { Item } from "components/Pages/Builds/BuildPageContainer";
import { ReactComponent as SoulsIcon } from "svg/deadlock-souls.svg";
import classNames from "classnames";
import { getDeadlockImagePath, shouldDisplayStat } from "lib/general-helper";

interface ItemTooltipProps {
  item: Item;
}

const ItemTooltip = (props: ItemTooltipProps) => {
  const { item } = props;

  const iconImage = getDeadlockImagePath(item.iconPath ?? "");

  // Component Items Data
  const componentItemsData = item?.componentItemsData;

  // Shop Bonus
  const shopBonus = item?.shopBonus;

  // Extract stats (same as in BuildOrder.tsx)
  const reloadTime = item.stats?.ReloadSpeedMultipler;
  const weaponFallOffRange = item.stats?.BonusAttackRangePercent;
  const bonusZoomPercent = item.stats?.BonusZoomPercent;
  const bulletShieldHealth = item.stats?.BulletShieldMaxHealth;
  const weaponDamage = item.stats?.BaseAttackDamagePercent;
  const passiveEffect = item?.description;
  const bonusWeaponDamage = item.stats?.LongRangeBonusWeaponPower;
  const bonusDamageMinRange = item.stats?.LongRangeBonusWeaponPowerMinRange;
  const bonusBulletVelocity = item.stats?.BonusBulletSpeedPercent;
  const bonusFireRate = item.stats?.BonusFireRate;
  const bonusAbilityCharges = item.stats?.BonusAbilityCharges;
  const cooldownReductionOnChargedAbilities = item.stats?.CooldownReductionOnChargedAbilities;
  const spiritShieldHealth = item.stats?.TechShieldMaxHealth;
  const attackDamageWhenShielded = item.stats?.AttackDamageWhenShielded;
  const fireRateWhenShielded = item.stats?.FireRateWhenShielded;
  const bonusSpiritWithMagicShield = item.stats?.BonusSpiritWithMagicShield;
  const cooldownReductionWithShield = item.stats?.CooldownReductionWithShield;
  const bonusHealth = item.stats?.BonusHealth;
  const meleeResist = item.stats?.MeleeResistPercent;
  const techPower = item.stats?.TechPower;
  const techResist = item.stats?.TechResist;
  const bonusMoveSpeed = item.stats?.BonusMoveSpeed;
  const bonusSprintSpeed = item.stats?.BonusSprintSpeed;
  const bonusHealthRegen = item.stats?.BonusHealthRegen;
  const slideDistance = item.stats?.SlideScale;
  const maxHealthLoss = item.stats?.MaxHealthLossPercent;
  const buffDuration = item.stats?.BuffDuration;
  const bonusClipSize = item.stats?.BonusClipSizePercent;
  const procChance = item.stats?.ProcChance;
  const critDamage = item.stats?.CritDamagePercent;
  const movementSlow = item.stats?.SlowPercent;
  const slowDuration = item.stats?.SlowDuration;
  const abilityLifestealPercentHero = item.stats?.AbilityLifestealPercentHero;
  const stamina = item.stats?.Stamina;
  const staminaCooldownReduction = item.stats?.StaminaCooldownReduction;
  const bulletResist = item.stats?.BulletResist;
  const bonusMeleeDamagePercent = item.stats?.BonusMeleeDamagePercent;
  const bulletLifestealPercent = item.stats?.BulletLifestealPercent;
  const techRangeMultiplier = item.stats?.TechRangeMultiplier;
  const spiritPower = item.stats?.SpiritPower;
  const bonusAbilityDurationPercent = item.stats?.BonusAbilityDurationPercent;
  const cooldownReduction = item.stats?.CooldownReduction;
  const imbuedTechRangeMultiplier = item.stats?.ImbuedTechRangeMultiplier;
  const cooldownBetweenChargeReduction = item.stats?.CooldownBetweenChargeReduction;
  const imbuedCooldownReduction = item.stats?.ImbuedCooldownReduction;
  const nonImbuedCooldownReduction = item.stats?.NonImbuedCooldownReduction;
  const imbuedBonusDuration = item.stats?.ImbuedBonusDuration;
  const nonImbuedBonusDuration = item.stats?.NonImbuedBonusDuration;
  const techArmorDamageReduction = item.stats?.TechArmorDamageReduction;

  return (
    <div className="tooltip-content items">
      <div className="tooltip-header">
        <div className="tooltip-left">
          <div>
            <div
              className={classNames("item-icon-container", {
                orange: item.type === "EItemSlotType_WeaponMod",
                green: item.type === "EItemSlotType_Armor",
                purple: item.type === "EItemSlotType_Tech",
              })}
            >
              <img
                src={`https://static.bigbrain.gg/deadlock/staticdata/panorama/images${iconImage}.png`}
                alt={item.name}
                className="tooltip-icon"
              />
            </div>
          </div>
          <div>
            <span className="tooltip-item-name">{item.name}</span>
          </div>
        </div>
        <div className="tooltip-right">
          <div className="cost-container">
            <SoulsIcon className="souls-icon" /> {item.cost}
          </div>
        </div>
      </div>
      {componentItemsData && componentItemsData.length > 0 && (
        <div className="tooltip-components">
          <p className="component-text">Components</p>
          {componentItemsData.map((componentItem, index) => (
            <div key={index} className="component-item">
              <div
                className={classNames("item-icon-container", {
                  orange: item.type === "EItemSlotType_WeaponMod",
                  green: item.type === "EItemSlotType_Armor",
                  purple: item.type === "EItemSlotType_Tech",
                })}
              >
                <img
                  src={`https://static.bigbrain.gg/deadlock/staticdata/panorama/images${getDeadlockImagePath(
                    componentItem.iconPath ?? "",
                  )}.png`}
                  alt={componentItem.name}
                  className="component-icon"
                />
              </div>
              <span
                className={classNames("component-name", {
                  orange: item.type === "EItemSlotType_WeaponMod",
                  green: item.type === "EItemSlotType_Armor",
                  purple: item.type === "EItemSlotType_Tech",
                })}
              >
                {componentItem.name}
              </span>
            </div>
          ))}
        </div>
      )}
      <div className="tooltip-stats">
        {shouldDisplayStat(shopBonus) && (
          <div className="tooltip-stat">
            <span className="stat-value">Shop Bonus:</span>
            <span className="stat-name"> {shopBonus}</span>
          </div>
        )}
        {shouldDisplayStat(reloadTime) && (
          <div className="tooltip-stat">
            <span className="stat-value">{reloadTime}%</span>
            <span className="stat-name"> Reload Time</span>
          </div>
        )}
        {shouldDisplayStat(weaponFallOffRange) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{weaponFallOffRange}%</span>
            <span className="stat-name"> Weapon Fall-off Range</span>
          </div>
        )}
        {shouldDisplayStat(stamina) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{stamina}</span>
            <span className="stat-name"> Stamina</span>
          </div>
        )}
        {shouldDisplayStat(staminaCooldownReduction) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{staminaCooldownReduction}%</span>
            <span className="stat-name"> Stamina Recovery</span>
          </div>
        )}
        {shouldDisplayStat(bonusZoomPercent) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{bonusZoomPercent}%</span>
            <span className="stat-name"> Weapon Zoom</span>
          </div>
        )}
        {shouldDisplayStat(bonusFireRate) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{bonusFireRate}%</span>
            <span className="stat-name"> Fire Rate</span>
          </div>
        )}
        {shouldDisplayStat(imbuedTechRangeMultiplier) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{imbuedTechRangeMultiplier}%</span>
            <span className="stat-name"> Imbued Ability Range</span>
          </div>
        )}
        {shouldDisplayStat(slideDistance) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{slideDistance}%</span>
            <span className="stat-name"> Slide Distance</span>
          </div>
        )}
        {shouldDisplayStat(cooldownBetweenChargeReduction) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{cooldownBetweenChargeReduction}%</span>
            <span className="stat-name"> Faster Time Between Charges</span>
          </div>
        )}
        {shouldDisplayStat(bulletShieldHealth) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{bulletShieldHealth}</span>
            <span className="stat-name"> Bullet Shield Health</span>
          </div>
        )}
        {shouldDisplayStat(bonusBulletVelocity) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{bonusBulletVelocity}%</span>
            <span className="stat-name"> Bullet Velocity</span>
          </div>
        )}
        {shouldDisplayStat(techArmorDamageReduction) && (
          <div className="tooltip-stat">
            <span className="stat-value">{techArmorDamageReduction}%</span>
            <span className="stat-name"> Spirit Resist on Spirit Damage</span>
          </div>
        )}
        {shouldDisplayStat(bonusClipSize) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{bonusClipSize}%</span>
            <span className="stat-name"> Ammo</span>
          </div>
        )}
        {shouldDisplayStat(bonusAbilityCharges) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{bonusAbilityCharges}</span>
            <span className="stat-name"> Bonus Ability Charges</span>
          </div>
        )}
        {shouldDisplayStat(cooldownReductionOnChargedAbilities) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{cooldownReductionOnChargedAbilities}%</span>
            <span className="stat-name"> Cooldown Reduction for Charged Abilities</span>
          </div>
        )}
        {shouldDisplayStat(spiritShieldHealth) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{spiritShieldHealth}</span>
            <span className="stat-name"> Spirit Shield Health</span>
          </div>
        )}
        {shouldDisplayStat(bonusMoveSpeed) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{bonusMoveSpeed}/s</span>
            <span className="stat-name"> Move Speed</span>
          </div>
        )}
        {shouldDisplayStat(imbuedCooldownReduction) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{imbuedCooldownReduction}%</span>
            <span className="stat-name"> Imbued Ability Cooldown Reduction</span>
          </div>
        )}
        {shouldDisplayStat(nonImbuedCooldownReduction) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{nonImbuedCooldownReduction}%</span>
            <span className="stat-name"> Non-Imbued Ability Cooldown Reduction</span>
          </div>
        )}
        {shouldDisplayStat(imbuedBonusDuration) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{imbuedBonusDuration}%</span>
            <span className="stat-name"> Imbued Ability Duration</span>
          </div>
        )}
        {shouldDisplayStat(nonImbuedBonusDuration) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{nonImbuedBonusDuration}%</span>
            <span className="stat-name"> Non-Imbued Ability Duration</span>
          </div>
        )}
        {shouldDisplayStat(buffDuration) && (
          <div className="tooltip-stat">
            <span className="stat-value">{buffDuration}s</span>
            <span className="stat-name"> Buff Duration</span>
          </div>
        )}
        {shouldDisplayStat(maxHealthLoss) && (
          <div className="tooltip-stat">
            <span className="stat-value">{maxHealthLoss}%</span>
            <span className="stat-name"> Max Health</span>
          </div>
        )}
        {shouldDisplayStat(bonusSprintSpeed) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{bonusSprintSpeed}/s</span>
            <span className="stat-name"> Sprint Speed</span>
          </div>
        )}
        {shouldDisplayStat(abilityLifestealPercentHero) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{abilityLifestealPercentHero}%</span>
            <span className="stat-name"> Spirit Lifesteal</span>
          </div>
        )}
        {shouldDisplayStat(cooldownReduction) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{cooldownReduction}%</span>
            <span className="stat-name"> Cooldown Reduction</span>
          </div>
        )}
        {shouldDisplayStat(bonusMeleeDamagePercent) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{bonusMeleeDamagePercent}%</span>
            <span className="stat-name"> Melee Damage</span>
          </div>
        )}
        {shouldDisplayStat(bulletLifestealPercent) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{bulletLifestealPercent}%</span>
            <span className="stat-name"> Bullet Lifesteal</span>
          </div>
        )}
        {shouldDisplayStat(bonusHealth) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{bonusHealth}</span>
            <span className="stat-name"> Bonus Health</span>
          </div>
        )}
        {shouldDisplayStat(bulletResist) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{bulletResist}%</span>
            <span className="stat-name"> Bullet Resist</span>
          </div>
        )}
        {shouldDisplayStat(spiritPower) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{spiritPower}</span>
            <span className="stat-name"> Spirit Power</span>
          </div>
        )}
        {shouldDisplayStat(bonusHealthRegen) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{bonusHealthRegen}</span>
            <span className="stat-name"> Health Regen</span>
          </div>
        )}
        {shouldDisplayStat(meleeResist) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{meleeResist}%</span>
            <span className="stat-name"> Melee Resist</span>
          </div>
        )}
        {shouldDisplayStat(weaponDamage) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{weaponDamage}%</span>
            <span className="stat-name"> Weapon Damage</span>
          </div>
        )}
        {shouldDisplayStat(techPower) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{techPower}</span>
            <span className="stat-name"> Spirit Power</span>
          </div>
        )}
        {shouldDisplayStat(techResist) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{techResist}%</span>
            <span className="stat-name"> Spirit Resist</span>
          </div>
        )}
        {shouldDisplayStat(techRangeMultiplier) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{techRangeMultiplier}%</span>
            <span className="stat-name"> Ability Range</span>
          </div>
        )}
        {shouldDisplayStat(bonusAbilityDurationPercent) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{bonusAbilityDurationPercent}%</span>
            <span className="stat-name"> Ability Duration</span>
          </div>
        )}
      </div>
      {passiveEffect && passiveEffect !== "undefined" && (
        <div className="tooltip-passive">
          <p className="passive-subheading">Passive</p>
          <p className="passive-info" dangerouslySetInnerHTML={{ __html: passiveEffect }}></p>
        </div>
      )}
      <div className="tooltip-bonus-effects">
        {shouldDisplayStat(procChance) && (
          <div className="tooltip-stat">
            <span className="stat-value">{procChance}%</span>
            <span className="stat-name"> Proc Chance</span>
          </div>
        )}
        {shouldDisplayStat(critDamage) && (
          <div className="tooltip-stat">
            <span className="stat-value">{critDamage}%</span>
            <span className="stat-name"> Bonus Crit Damage</span>
          </div>
        )}
        {shouldDisplayStat(movementSlow) && (
          <div className="tooltip-stat">
            <span className="stat-value">{movementSlow}%</span>
            <span className="stat-name"> Movement Slow</span>
          </div>
        )}
        {shouldDisplayStat(slowDuration) && (
          <div className="tooltip-stat">
            <span className="stat-value">{slowDuration}s</span>
            <span className="stat-name"> Slow Duration</span>
          </div>
        )}
        {shouldDisplayStat(bonusWeaponDamage) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{bonusWeaponDamage}%</span>
            <span className="stat-name"> Bonus Weapon Damage</span>
          </div>
        )}
        {shouldDisplayStat(bonusDamageMinRange) && (
          <div className="tooltip-stat">
            <span className="stat-value">{bonusDamageMinRange}</span>
            <span className="stat-name"> Minimum Distance</span>
          </div>
        )}
        {shouldDisplayStat(bonusSpiritWithMagicShield) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{bonusSpiritWithMagicShield}</span>
            <span className="stat-name"> Spirit Power While Shielded</span>
          </div>
        )}
        {shouldDisplayStat(cooldownReductionWithShield) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{cooldownReductionWithShield}%</span>
            <span className="stat-name"> Cooldown Reduction While Shielded</span>
          </div>
        )}
        {shouldDisplayStat(attackDamageWhenShielded) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{attackDamageWhenShielded}%</span>
            <span className="stat-name"> Weapon Damage While Shielded</span>
          </div>
        )}
        {shouldDisplayStat(fireRateWhenShielded) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{fireRateWhenShielded}%</span>
            <span className="stat-name"> Fire Rate While Shielded</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ItemTooltip;
